import React, { useState, useEffect } from 'react';
import InputMask from 'react-input-mask-next';
import { CircleAlert as AlertCircle, UserCheck, RefreshCw, ShieldBan } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { ClientPersonalData, ClientType } from './types';
import { formatCurrency } from './types';

interface Props {
  data: ClientPersonalData;
  clientType: ClientType;
  onComplete: (data: ClientPersonalData) => void;
  onClientFound?: (contract: any) => void;
}

interface BlacklistInfo {
  client_name: string;
  reason: string;
  created_at: string;
}

export default function ClientDataStep({ data, clientType, onComplete, onClientFound }: Props) {
  const { user } = useAuth();
  const [form, setForm] = useState<ClientPersonalData>(data);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasUnpaid, setHasUnpaid] = useState(false);
  const [pendingByUnit, setPendingByUnit] = useState<{ unitName: string; balance: number }[]>([]);
  const [existingClient, setExistingClient] = useState<any>(null);
  const [checkingCpf, setCheckingCpf] = useState(false);
  const [blacklistInfo, setBlacklistInfo] = useState<BlacklistInfo | null>(null);

  const up = (field: keyof ClientPersonalData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  useEffect(() => {
    const digits = form.cpf.replace(/\D/g, '');
    if (digits.length === 11) {
      checkCpf(digits);
    } else {
      setHasUnpaid(false);
      setPendingByUnit([]);
      setExistingClient(null);
      setBlacklistInfo(null);
    }
  }, [form.cpf]);

  const checkCpf = async (digits: string) => {
    setCheckingCpf(true);
    try {
      // Check blacklist first
      const { data: blacklistEntry } = await supabase
        .from('cpf_blacklist')
        .select('client_name, reason, created_at')
        .eq('cpf', digits)
        .maybeSingle();

      if (blacklistEntry) {
        setBlacklistInfo(blacklistEntry);
        setHasUnpaid(false);
        setPendingByUnit([]);
        setExistingClient(null);
        return;
      }

      setBlacklistInfo(null);

      const { data: balances } = await supabase
        .rpc('check_client_pending_balance', { p_cpf: digits });

      const pendingList = (balances || []).map((b: any) => ({
        unitName: b.unit_name,
        balance: Number(b.balance),
      })).filter((u: any) => u.balance > 0);

      if (pendingList.length > 0) {
        setHasUnpaid(true);
        setPendingByUnit(pendingList);
        setExistingClient(null);
        return;
      }

      setHasUnpaid(false);
      setPendingByUnit([]);

      const { data: paid } = await supabase
        .from('contracts')
        .select('*')
        .eq('cpf', digits)
        .eq('unit_id', user?.unit_id)
        .eq('payment_status', 'paid')
        .order('created_at', { ascending: false })
        .limit(1);

      if (paid && paid.length > 0) {
        setExistingClient(paid[0]);
      } else {
        setExistingClient(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCheckingCpf(false);
    }
  };

  const handleLoadPreviousData = () => {
    if (!existingClient) return;
    setForm(prev => ({
      ...prev,
      name: existingClient.client_name,
      phone: existingClient.phone,
      pixKey: existingClient.pix_key || '',
      bank: existingClient.bank || '',
      serviceEntryTime: existingClient.service_entry_time || '',
      serviceExitTime: existingClient.service_exit_time || '',
    }));
    onClientFound?.(existingClient);
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Nome obrigatório';
    if (form.cpf.replace(/\D/g, '').length !== 11) errs.cpf = 'CPF inválido';
    if (form.phone.replace(/\D/g, '').length < 10) errs.phone = 'Telefone inválido';
    if (!form.pixKey.trim()) errs.pixKey = 'Chave PIX obrigatória';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleContinue = () => {
    if (!validate()) return;
    onComplete(form);
  };

  const field = (
    label: string,
    key: keyof ClientPersonalData,
    placeholder?: string,
    required = false
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}{required && ' *'}
      </label>
      <input
        type="text"
        value={form[key] as string}
        onChange={up(key)}
        placeholder={placeholder}
        className={`w-full px-4 py-3 border rounded-xl text-base transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
          errors[key] ? 'border-red-400 bg-red-50' : 'border-gray-300'
        }`}
      />
      {errors[key] && <p className="text-red-500 text-xs mt-1">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Dados do Cliente</h2>
        <p className="text-gray-600 text-sm">Informações pessoais e financeiras do cliente.</p>
      </div>

      {hasUnpaid && (
        <div className="bg-red-50 border border-red-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-red-200">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm font-semibold text-red-800">Cliente com valores em aberto</p>
          </div>
          <div className="px-4 py-3 space-y-2">
            {pendingByUnit.map((item, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-red-700">{item.unitName}</span>
                <span className="text-sm font-semibold text-red-800 tabular-nums">
                  {formatCurrency(item.balance)}
                </span>
              </div>
            ))}
            {pendingByUnit.length > 1 && (
              <div className="flex items-center justify-between pt-2 border-t border-red-200">
                <span className="text-sm font-medium text-red-800">Total</span>
                <span className="text-sm font-bold text-red-900 tabular-nums">
                  {formatCurrency(pendingByUnit.reduce((s, u) => s + u.balance, 0))}
                </span>
              </div>
            )}
            <p className="text-xs text-red-600 pt-1">Não é possível criar um novo contrato enquanto houver valores em aberto.</p>
          </div>
        </div>
      )}

      {blacklistInfo && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-red-100 border-b border-red-300">
            <ShieldBan className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm font-bold text-red-800">CPF NA BLACKLIST</p>
          </div>
          <div className="px-4 py-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-red-700">Nome registrado:</span>
              <span className="text-sm font-semibold text-red-800">{blacklistInfo.client_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-red-700">Motivo:</span>
              <span className="text-sm font-semibold text-red-800">{blacklistInfo.reason}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-red-700">Data da exclusao:</span>
              <span className="text-sm font-semibold text-red-800">
                {new Date(blacklistInfo.created_at).toLocaleDateString('pt-BR')}
              </span>
            </div>
            <p className="text-xs text-red-600 font-medium pt-1">
              Este CPF foi excluido do sistema e esta bloqueado para novos cadastros.
            </p>
          </div>
        </div>
      )}

      {existingClient && !hasUnpaid && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-blue-100">
            <UserCheck className="w-5 h-5 text-blue-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-800">Cliente com contrato anterior quitado</p>
              <p className="text-xs text-blue-600 mt-0.5">
                Dados cadastrados anteriormente estão disponíveis.
              </p>
            </div>
          </div>
          <div className="px-4 py-3">
            <button
              type="button"
              onClick={handleLoadPreviousData}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Carregar dados do contrato anterior
            </button>
            <p className="text-xs text-blue-500 text-center mt-2">
              Enderecos e contatos tambem serao pre-preenchidos — voce pode editar em cada etapa.
            </p>
          </div>
        </div>
      )}

      {field('Nome completo', 'name', 'Nome do cliente', true)}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">CPF *</label>
          <InputMask
            mask="999.999.999-99"
            value={form.cpf}
            onChange={(e) => setForm(prev => ({ ...prev, cpf: e.target.value }))}
            className={`w-full px-4 py-3 border rounded-xl text-base transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.cpf ? 'border-red-400 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="000.000.000-00"
          />
          {errors.cpf && <p className="text-red-500 text-xs mt-1">{errors.cpf}</p>}
          {checkingCpf && <p className="text-gray-400 text-xs mt-1">Verificando...</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefone *</label>
          <InputMask
            mask="(99) 99999-9999"
            value={form.phone}
            onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
            className={`w-full px-4 py-3 border rounded-xl text-base transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.phone ? 'border-red-400 bg-red-50' : 'border-gray-300'
            }`}
            placeholder="(00) 00000-0000"
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Chave PIX *</label>
          <input
            type="text"
            value={form.pixKey}
            onChange={up('pixKey')}
            placeholder="CPF, celular, e-mail ou chave"
            className={`w-full px-4 py-3 border rounded-xl text-base transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.pixKey ? 'border-red-400 bg-red-50' : 'border-gray-300'
            }`}
          />
          {errors.pixKey && <p className="text-red-500 text-xs mt-1">{errors.pixKey}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Banco</label>
          <input
            type="text"
            value={form.bank}
            onChange={up('bank')}
            placeholder="Nome do banco"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {clientType !== 'autonomous' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Entrada no serviço</label>
            <input
              type="time"
              value={form.serviceEntryTime}
              onChange={up('serviceEntryTime')}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Saída do serviço</label>
            <input
              type="time"
              value={form.serviceExitTime}
              onChange={up('serviceExitTime')}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      <button
        onClick={handleContinue}
        disabled={hasUnpaid || !!blacklistInfo}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-base hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Continuar
      </button>
    </div>
  );
}
